import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { ImpactCard } from "@/components/impactReport/ImpactCard";
import { resolveImpactReportErrorMessage } from "@/components/impactReport/impact-report-error";
import { GfgSelect, type GfgSelectOption } from "@/components/ui/GfgSelect";
import {
  IMPACT_REPORT_CARD_DEFAULT_SORT_MODE,
  IMPACT_REPORT_CARD_EMPTY_STATE_TEXT,
  IMPACT_REPORT_CARD_ERROR_STATE_TEXT,
  IMPACT_REPORT_CARD_HEADING,
  IMPACT_REPORT_CARD_SORT_LABEL,
  IMPACT_REPORT_CARD_SORT_MODE_ENTRIES,
  IMPACT_REPORT_UNAVAILABLE_STATE_BODY,
  IMPACT_REPORT_UNAVAILABLE_STATE_TITLE,
} from "@/constants/impactReport";
import type { ImpactReport } from "@/interfaces/impactReport";
import type { ImpactReportCardSortMode } from "@/interfaces/impactReportView";
import {
  describeImpactReportError,
  flattenImpactReportCardRows,
  isImpactReportUnavailableError,
  sortImpactReportCardRows,
} from "@/utils/impact-report-helper";

interface ImpactCardGridProps {
  /**
   * The report the page already fetched — this component NEVER calls `useImpactReport` itself.
   * That is what lets slice 5's PDF export reuse this exact grid against a report it already
   * holds in memory, with `isLoading`/`isError` simply held at their resting `false` state.
   */
  report: ImpactReport | undefined;
  /** Forwarded straight from the page's `useImpactReport()` call (or `false` when reused for PDF). */
  isLoading: boolean;
  /** Forwarded straight from the page's `useImpactReport()` call (or `false` when reused for PDF). */
  isError: boolean;
  /** The hook's own `error`, when `isError` is true. Never rendered raw — see `describeImpactReportError`. */
  error?: unknown;
}

/**
 * "Explore your Impact" — the complete record. Every impact type in `causeData.flatMap(c =>
 * c.impactTypes)` gets a tile; nothing is filtered by value, unit, image or register. That is
 * what distinguishes this view from the hero, which prints only its top six per cause: the hero
 * decides what prints, this view decides nothing.
 *
 * Reads `statements.narrative` exclusively (via task 1's `flattenImpactReportCardRows`, which
 * already carries the register read per row) — never `statements.declarative`, and never
 * `summary.impactTypes.numTypes` or `causeData.length` as a check against this view's own
 * counts. Both comparisons legitimately differ on correct data; the contract forbids encoding
 * either as an invariant.
 *
 * Sorting is entirely client-side: both registers ship on every metric precisely so this toggle
 * costs no refetch, no loading state and no network request of any kind.
 *
 * DOES NOT FETCH. The page owns `useImpactReport` and hands down whichever state it is
 * currently in, so this component renders all three: loading, error, and the grid itself
 * (empty or populated).
 */
export function ImpactCardGrid({ report, isLoading, isError, error }: ImpactCardGridProps) {
  const [sortMode, setSortMode] = useState<ImpactReportCardSortMode>(
    IMPACT_REPORT_CARD_DEFAULT_SORT_MODE,
  );

  // Logged with context, per this repo's observability expectations — never swallowed, and never
  // left to fall through to the empty grid, which would tell a client "you have no impact" when
  // the truth is "we could not reach the service". Keyed on `isError`/`error` so one failed fetch
  // logs exactly once rather than once per render.
  useEffect(() => {
    if (!isError) return;
    console.error(
      "ImpactCardGrid: client impact report failed to load",
      describeImpactReportError(error),
    );
  }, [isError, error]);

  // `meta.warnings` is non-fatal by contract (a missing organization mirror row still serves a
  // valid report). Logged for visibility; NEVER blocks the render and NEVER becomes a
  // user-facing error banner.
  useEffect(() => {
    if (!report) return;
    const { warnings } = report.meta;
    if (warnings.length === 0) return;
    console.warn("ImpactCardGrid: client impact report returned non-fatal warnings", { warnings });
  }, [report]);

  // Flatten, then sort — task 1's helpers, never reimplemented here. `causeData` is the whole
  // input; every impact type in it reaches a row.
  const rows = useMemo(
    () => (report ? flattenImpactReportCardRows(report.data.causeData) : []),
    [report],
  );
  const sortedRows = useMemo(() => sortImpactReportCardRows(rows, sortMode), [rows, sortMode]);

  const sortOptions: GfgSelectOption[] = useMemo(
    () =>
      IMPACT_REPORT_CARD_SORT_MODE_ENTRIES.map(([mode, label]) => ({
        value: mode,
        label,
      })),
    [],
  );

  const handleSortModeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    // The select's own `value` is always one of `IMPACT_REPORT_CARD_SORT_MODE_ENTRIES`' keys —
    // its options are built from that same array above — so this narrowing cannot go stale.
    setSortMode(event.target.value as ImpactReportCardSortMode);
  };

  if (isLoading) {
    return <ImpactCardGridLoadingState />;
  }

  // A resolved-but-payload-less state is treated the same as a transport failure: a report that
  // never surfaced its causes must never be read as "this account has no impact".
  if (isError || !report) {
    return <ImpactCardGridErrorState error={error} />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: "var(--font-crimson)",
            fontSize: "32px",
            fontWeight: 400,
            color: "var(--color-rodeo-black)",
            margin: 0,
          }}
        >
          {IMPACT_REPORT_CARD_HEADING}
        </Typography>

        {sortedRows.length > 0 && (
          <Box sx={{ width: "220px", maxWidth: "100%" }}>
            <GfgSelect
              label={IMPACT_REPORT_CARD_SORT_LABEL}
              options={sortOptions}
              value={sortMode}
              onChange={handleSortModeChange}
            />
          </Box>
        )}
      </Box>

      {sortedRows.length === 0 ? (
        <ImpactCardGridEmptyState />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "36px 16px",
            // Every implicit row track shares the same `1fr` size. With the grid's own height
            // left indefinite (nothing here sets one), the `fr` sizing algorithm resolves that
            // shared size to whatever the tallest row track needs for its content, then applies
            // THAT size to every row track — not just the row that needed it. So a short card in
            // row 1 ends up matched to the tallest card anywhere in the grid, not merely the
            // tallest card in its own row. `alignItems` is left at the grid default of `stretch`
            // (no override here) so each card actually fills the track height it is handed
            // rather than sitting short inside a track that's now taller than its own content.
            gridAutoRows: "1fr",
            "@media (max-width: 1100px)": {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
            "@media (max-width: 680px)": {
              // At one column every card IS its own row, so there is no neighbour left to be
              // uneven with. Release the shared track size here — otherwise the tallest card in
              // the entire report would pad out every short card on a phone, one column wide.
              gridTemplateColumns: "1fr",
              gridAutoRows: "auto",
            },
          }}
        >
          {sortedRows.map((row) => (
            <ImpactCard key={row.cardKey} row={row} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/** A quiet placeholder matching the rest of the report's loading language. */
function ImpactCardGridLoadingState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "320px",
        gap: 2,
      }}
    >
      <CircularProgress sx={{ color: "var(--color-dust-ink)" }} />
      <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)" }}>
        Loading your impact…
      </Typography>
    </Box>
  );
}

interface ImpactCardGridErrorStateProps {
  error: unknown;
}

/**
 * Surfaces the failure rather than falling through to the empty grid — an empty state here would
 * read as "you have no impact" when the truth is "we could not reach the service".
 *
 * Splits the same way as the page's error state: `isImpactReportUnavailableError` gets the
 * "temporarily unavailable" copy; every other failure keeps the generic title and the API's own
 * message.
 */
function ImpactCardGridErrorState({ error }: ImpactCardGridErrorStateProps) {
  const isUnavailable = isImpactReportUnavailableError(error);
  return (
    <Box
      role="alert"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "260px",
        gap: 1,
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
        {isUnavailable ? IMPACT_REPORT_UNAVAILABLE_STATE_TITLE : "We couldn't load your impact"}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)", maxWidth: "360px" }}>
        {isUnavailable
          ? IMPACT_REPORT_UNAVAILABLE_STATE_BODY
          : resolveImpactReportErrorMessage(error, IMPACT_REPORT_CARD_ERROR_STATE_TEXT)}
      </Typography>
    </Box>
  );
}

/**
 * The report resolved with no impact types at all. Distinct from — and reachable only alongside —
 * the page's own empty state: `pages/ImpactReport.tsx` already intercepts `causeData.length === 0`
 * before this component ever mounts, so by the time this grid renders, `causeData` is non-empty and
 * this can only fire on the residual case, `causeData` non-empty but flattening to zero rows. Never
 * collapse the two into one check — the page cannot see this component's flattened row count
 * without reimplementing it.
 */
function ImpactCardGridEmptyState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "260px",
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)", maxWidth: "420px" }}>
        {IMPACT_REPORT_CARD_EMPTY_STATE_TEXT}
      </Typography>
    </Box>
  );
}
